/**
 * Inline styles shared by the Earnings Corrections components (Design System
 * tokens only — see scripts/check-tokens.sh).
 */

import React from 'react';

export type NoticeTone = 'warning' | 'danger' | 'info' | 'success' | 'neutral';

const NOTICE: Record<NoticeTone, { bg: string; fg: string; border: string }> = {
  warning: { bg: 'var(--warning-soft)', fg: 'var(--fg-1)', border: 'var(--cg-saffron-200)' },
  danger: { bg: 'var(--danger-soft)', fg: 'var(--danger)', border: 'var(--cg-terra-200)' },
  info: { bg: 'var(--info-soft)', fg: 'var(--fg-1)', border: 'var(--cg-teal-200)' },
  success: { bg: 'var(--success-soft)', fg: 'var(--fg-1)', border: 'var(--cg-forest-200)' },
  neutral: { bg: 'var(--bg-surface-2)', fg: 'var(--fg-2)', border: 'var(--border-1)' },
};

export const noticeStyle = (tone: NoticeTone): React.CSSProperties => ({
  background: NOTICE[tone].bg,
  color: NOTICE[tone].fg,
  border: `1px solid ${NOTICE[tone].border}`,
  borderRadius: 'var(--radius-md)',
  padding: '10px 12px',
  fontSize: 13,
  lineHeight: 1.45,
});

export const cs: Record<string, React.CSSProperties> = {
  section: {
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    padding: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--fg-1)' },
  subtitle: { margin: '4px 0 0', fontSize: 12.5, color: 'var(--fg-3)', maxWidth: 720, lineHeight: 1.45 },
  controls: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  select: {
    padding: '8px 12px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
  },
  input: {
    width: '100%',
    padding: '8px 11px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    boxSizing: 'border-box',
  },
  numInput: {
    width: 112,
    padding: '6px 8px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 12.5,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    minHeight: 72,
    padding: '8px 11px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
    marginBottom: 4,
  },
  help: { fontSize: 11.5, color: 'var(--fg-4)', marginTop: 4, lineHeight: 1.4 },
  field: { display: 'flex', flexDirection: 'column', gap: 0 },
  checkboxLabel: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--fg-2)', cursor: 'pointer' },
  muted: { color: 'var(--fg-3)' },
  small: { fontSize: 12, color: 'var(--fg-3)' },
  mono: {
    fontSize: 12,
    fontFamily: 'var(--font-mono)',
    background: 'var(--cg-neutral-50)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-xs)',
  },
  subheading: { margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' },
  kvGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
  },
  kvLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  kvValue: { fontSize: 13.5, color: 'var(--fg-1)', marginTop: 2, fontVariantNumeric: 'tabular-nums' },
  stack: { display: 'flex', flexDirection: 'column', gap: 16 },
  row: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  list: { margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 },
  loadingRow: { display: 'flex', justifyContent: 'center', padding: '24px 0' },
  linkButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    color: 'var(--fg-link)',
    cursor: 'pointer',
    font: 'inherit',
    textDecoration: 'underline',
  },
};

/** Green for increases, red for decreases, muted for none. */
export const deltaColor = (cents: number): string => (cents > 0 ? 'var(--success)' : cents < 0 ? 'var(--danger)' : 'var(--fg-3)');
